import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Camera, Check, ClipboardList, IndianRupee, MessageSquare,
  Phone, RotateCcw, User, UserRoundCheck, X,
} from 'lucide-react';
import { useSelector } from 'react-redux';

const API = process.env.REACT_APP_API_URL;

const inputBase = 'w-full border rounded-lg pl-3 pr-10 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white';
const inputCls = 'w-full border border-gray-300 rounded-lg pl-3 pr-10 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white';
const inputErr = `${inputBase} border-red-400`;
const maxDocumentFileSize = 5 * 1024 * 1024;

const emptyForm = {
  caseName: '',
  mobileNumber: '',
  referredBy: '',
  loanType: '',
  amountRequired: '',
  remarks: '',
  callStatus: '',
  callBackDate: '',
  dispositionChecked: false,
  dispositionRemark: '',
};

const callStatusOptions = [
  { value: 'not_interested', label: 'Not Interested' },
  { value: 'warm_lead',      label: 'Warm Lead' },
  { value: 'call_back',      label: 'Call Back' },
  { value: 'cold_lead',      label: 'Cold Lead' },
  { value: 'not_connected',  label: 'Not Connected' },
];

const phoneOk = v => /^[0-9]{10}$/.test(String(v || '').trim());
const amountOk = v => v === '' || (!Number.isNaN(Number(v)) && Number(v) >= 0);

const rules = {
  caseName:       v => v.trim() ? '' : 'Case name is required.',
  mobileNumber:   v => phoneOk(v) ? '' : 'Mobile number must be 10 digits.',
  amountRequired: v => amountOk(v) ? '' : 'Amount must be a positive number.',
};

const fieldIcons = {
  caseName:       User,
  mobileNumber:   Phone,
  referredBy:     UserRoundCheck,
  loanType:       ClipboardList,
  amountRequired: IndianRupee,
  remarks:        MessageSquare,
};

// ── Shared primitives ─────────────────────────────────────────────────────────
const FieldIcon = ({ field, multiline = false }) => {
  const Icon = fieldIcons[field];
  if (!Icon) return null;
  return (
    <Icon
      size={18} strokeWidth={2}
      className={`pointer-events-none absolute right-3 ${multiline ? 'top-3' : 'top-1/2 -translate-y-1/2'} text-gray-400`}
      aria-hidden="true"
    />
  );
};

const Field = ({ field, label, touched, errs, focusedField, children }) => (
  <div>
    {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
    {children}
    {touched[field] && focusedField !== field && errs[field] && (
      <p className="text-xs text-red-600 mt-1">{errs[field]}</p>
    )}
  </div>
);

const TextInput = ({ field, label, type = 'text', placeholder, form, touched, errs, focusedField, onChange, onBlur, onFocus }) => (
  <Field field={field} label={label} touched={touched} errs={errs} focusedField={focusedField}>
    <div className="relative">
      <input
        type={type}
        className={touched[field] && focusedField !== field && errs[field] ? inputErr : inputCls}
        placeholder={placeholder || label}
        value={form[field]}
        onChange={e => onChange(field, e.target.value)}
        onFocus={() => onFocus(field)}
        onBlur={e => onBlur(field, e.target.value)}
        inputMode={field === 'mobileNumber' ? 'numeric' : undefined}
      />
      <FieldIcon field={field} />
    </div>
  </Field>
);

const AmountInput = props => <TextInput {...props} type="number" placeholder="0" />;

// ── Camera Modal ──────────────────────────────────────────────────────────────
const CameraModal = ({ field, label, onCapture, onClose }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [error, setError] = useState('');
  const [capturedFile, setCapturedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    let stream;
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false });
        if (videoRef.current) videoRef.current.srcObject = stream;
      } catch (err) {
        setError(err?.message || 'Camera is not available on this device.');
      }
    })();
    return () => { stream?.getTracks().forEach(t => t.stop()); };
  }, []);

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const capture = () => {
    const video = videoRef.current; const canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth) return;
    canvas.width = video.videoWidth; canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(blob => {
      if (!blob) return;
      const file = new File([blob], `${field}-${Date.now()}.jpg`, { type: 'image/jpeg' });
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setCapturedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }, 'image/jpeg', 0.9);
  };

  const retake = () => { if (previewUrl) URL.revokeObjectURL(previewUrl); setCapturedFile(null); setPreviewUrl(''); };
  const usePhoto = () => { if (!capturedFile) return; onCapture(field, capturedFile); onClose(); };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-800">Capture {label || field}</h3>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-800 text-2xl leading-none">&times;</button>
        </div>
        <div className="bg-black">
          {error ? <div className="p-6 text-sm text-white text-center">{error}</div>
            : previewUrl ? <img src={previewUrl} alt="preview" className="w-full aspect-[3/4] object-cover" />
            : <video ref={videoRef} autoPlay playsInline muted className="w-full aspect-[3/4] object-cover" />}
          <canvas ref={canvasRef} className="hidden" />
        </div>
        <div className="grid grid-cols-2 gap-2 p-4 sm:flex sm:justify-end">
          <button type="button" onClick={previewUrl ? retake : onClose} className="inline-flex items-center justify-center border border-gray-300 hover:bg-gray-50 text-gray-700 w-11 h-11 rounded-lg transition-colors">
            {previewUrl ? <RotateCcw size={16} /> : <X size={16} />}
          </button>
          {!previewUrl && (
            <button type="button" onClick={capture} disabled={!!error} className="inline-flex items-center justify-center border border-blue-600 text-blue-700 hover:bg-blue-50 disabled:border-gray-300 disabled:text-gray-400 w-11 h-11 rounded-lg transition-colors">
              <Camera size={16} />
            </button>
          )}
          {previewUrl && <>
            <button type="button" onClick={onClose} className="inline-flex items-center justify-center border border-gray-300 hover:bg-gray-50 text-gray-700 w-11 h-11 rounded-lg transition-colors"><X size={16} /></button>
            <button type="button" onClick={usePhoto} className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white w-11 h-11 rounded-lg transition-colors"><Check size={16} /></button>
          </>}
        </div>
      </div>
    </div>
  );
};

// ── Document Capture Card ─────────────────────────────────────────────────────
const DocumentCapture = ({ field, label, file, error, onChange, onClear, onCamera, onError }) => (
  <div className={`border rounded-lg bg-gray-50 p-3 ${error ? 'border-red-300' : 'border-gray-200'}`}>
    <label className="block text-sm font-medium text-gray-700 mb-2">{label || field}</label>
    <div className="grid grid-cols-1 gap-2 sm:flex sm:flex-wrap sm:items-center">
      <button type="button" onClick={() => onCamera(field)} className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
        Open Camera
      </button>
      <label className="inline-flex items-center justify-center border border-gray-300 hover:bg-white text-gray-700 text-sm font-medium px-3 py-2 rounded-lg transition-colors cursor-pointer">
        Choose Image
        <input type="file" accept="image/*" className="hidden" onChange={e => {
          e.preventDefault(); e.stopPropagation();
          const f = e.target.files?.[0] || null;
          if (f && f.size > maxDocumentFileSize) { e.target.value = ''; onError?.(`${label || field} must be 5 MB or less.`); return; }
          onChange(field, f);
        }} />
      </label>
      {file && (
        <button type="button" onClick={() => onClear(field)} className="border border-gray-300 hover:bg-white text-gray-700 text-sm font-medium px-3 py-2 rounded-lg transition-colors">
          Remove
        </button>
      )}
    </div>
    <p className="text-xs text-gray-500 mt-2 truncate">{file ? file.name : 'No image selected'}</p>
    {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
  </div>
);

// ── Standalone Caller Form Page ───────────────────────────────────────────────
export default function CallerForm() {
  const { userInfo } = useSelector(s => s.userLogin || {});
  const authConfig = { headers: userInfo?.token ? { Authorization: `Bearer ${userInfo.token}` } : {} };
  const draftLocalKey = `callerDraft_${userInfo?._id || 'anon'}`;

  const [form, setForm] = useState(emptyForm);
  const [formKey, setFormKey] = useState(0);
  const [errs, setErrs] = useState({});
  const [touched, setTouched] = useState({});
  const [reqDocs, setReqDocs] = useState({});
  const [documentErrs, setDocumentErrs] = useState({});
  const [cameraField, setCameraField] = useState('');
  const [focusedField, setFocusedField] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [autoSaving, setAutoSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [loanTypes, setLoanTypes] = useState([]);
  const [toast, setToast] = useState(null);

  const autoSaveTimer = useRef(null);
  const latestFormRef = useRef(form);
  const latestDraftIdRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/loan-types`, authConfig)
      .then(({ data }) => setLoanTypes(data))
      .catch(() => {});
  }, []); // eslint-disable-line

  // Restore draft from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(draftLocalKey);
    if (saved) {
      try {
        const { draftId: sid, form: sf } = JSON.parse(saved);
        const restored = { ...emptyForm, ...sf };
        setForm(restored);
        latestFormRef.current = restored;
        if (sid) { setDraftId(sid); latestDraftIdRef.current = sid; }
      } catch {}
    }
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
  }, []); // eslint-disable-line

  useEffect(() => { latestFormRef.current = form; }, [form]);
  useEffect(() => { latestDraftIdRef.current = draftId; }, [draftId]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const performAutoSave = async () => {
    setAutoSaving(true);
    try {
      const body = new FormData();
      body.append('isDraft', 'true');
      Object.entries(latestFormRef.current).forEach(([k, v]) => body.append(k, v));
      let id = latestDraftIdRef.current;
      if (id) {
        await axios.put(`${API}/callers/${id}`, body, authConfig);
      } else {
        const { data } = await axios.post(`${API}/callers`, body, authConfig);
        id = data.caller._id;
        setDraftId(id);
        latestDraftIdRef.current = id;
      }
      localStorage.setItem(draftLocalKey, JSON.stringify({ draftId: id, form: latestFormRef.current }));
      setLastSaved(new Date());
    } catch {} finally { setAutoSaving(false); }
  };

  const onChange = (field, value) => {
    const v = field === 'mobileNumber' ? value.replace(/\D/g, '').slice(0, 10) : value;
    const nextForm = { ...form, [field]: v };
    setForm(nextForm);
    latestFormRef.current = nextForm;
    if (field === 'loanType') { setReqDocs({}); setDocumentErrs({}); }
    localStorage.setItem(draftLocalKey, JSON.stringify({ draftId: latestDraftIdRef.current, form: nextForm }));
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      if (latestFormRef.current.caseName?.trim()) performAutoSave();
    }, 8000);
  };

  const onBlur = (field, value = form[field]) => {
    setFocusedField('');
    const v = field === 'mobileNumber' ? value.replace(/\D/g, '').slice(0, 10) : value;
    setTouched(prev => ({ ...prev, [field]: true }));
    setErrs(prev => ({ ...prev, [field]: rules[field] ? rules[field](v) : '' }));
  };

  const submit = async e => {
    e.preventDefault();
    if (autoSaveTimer.current) { clearTimeout(autoSaveTimer.current); autoSaveTimer.current = null; }
    const next = {};
    Object.keys(rules).forEach(k => { const m = rules[k](form[k]); if (m) next[k] = m; });
    setErrs(next); setTouched(Object.fromEntries(Object.keys(rules).map(k => [k, true])));
    if (Object.keys(next).length) return;

    const selectedLT = loanTypes.find(lt => lt.name === form.loanType);
    const requiredDocs = selectedLT?.requiredDocuments || [];
    const body = new FormData();
    body.append('isDraft', 'false');
    Object.entries(form).forEach(([k, v]) => body.append(k, v));
    requiredDocs.forEach((docName, i) => {
      body.append(`reqDocName_${i}`, docName);
      if (reqDocs[docName]) body.append(`reqDoc_${i}`, reqDocs[docName]);
    });

    setSubmitting(true);
    try {
      if (draftId) {
        await axios.put(`${API}/callers/${draftId}`, body, authConfig);
      } else {
        await axios.post(`${API}/callers`, body, authConfig);
      }
      localStorage.removeItem(draftLocalKey);
      setSubmitted(true);
    } catch (err) {
      setToast({ type: 'error', msg: err?.response?.data?.message || err?.message || 'Failed to submit.' });
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setForm(emptyForm);
    latestFormRef.current = emptyForm;
    setDraftId(null);
    latestDraftIdRef.current = null;
    setErrs({}); setTouched({}); setReqDocs({}); setDocumentErrs({});
    setFormKey(k => k + 1);
    setSubmitted(false);
    setDraftSaved(false);
    setLastSaved(null);
  };

  const skipAsDraft = async () => {
    if (autoSaveTimer.current) { clearTimeout(autoSaveTimer.current); autoSaveTimer.current = null; }
    setSkipping(true);
    try {
      const body = new FormData();
      body.append('isDraft', 'true');
      Object.entries(latestFormRef.current).forEach(([k, v]) => body.append(k, v));
      if (latestDraftIdRef.current) {
        await axios.put(`${API}/callers/${latestDraftIdRef.current}`, body, authConfig);
      } else {
        await axios.post(`${API}/callers`, body, authConfig);
      }
      localStorage.removeItem(draftLocalKey);
      setDraftSaved(true);
    } catch (err) {
      setToast({ type: 'error', msg: err?.response?.data?.message || err?.message || 'Failed to save draft.' });
    } finally {
      setSkipping(false);
    }
  };

  // ── Success screen ────────────────────────────────────────────────────────
  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-10">
          <span className="text-sm font-semibold text-gray-700">Caller Enquiry Form</span>
          <span className="text-xs text-gray-500">{userInfo?.name || ''}</span>
        </div>
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 max-w-sm w-full text-center">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check size={28} className="text-green-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-800 mb-2">Enquiry Submitted</h2>
            <p className="text-sm text-gray-500 mb-6">Your caller enquiry has been recorded successfully.</p>
            <button
              onClick={resetForm}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-6 py-2 rounded-lg transition-colors"
            >
              Submit Another
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Draft saved screen ────────────────────────────────────────────────────
  if (draftSaved) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-10">
          <span className="text-sm font-semibold text-gray-700">Caller Enquiry Form</span>
          <span className="text-xs text-gray-500">{userInfo?.name || ''}</span>
        </div>
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 max-w-sm w-full text-center">
            <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check size={28} className="text-orange-500" />
            </div>
            <h2 className="text-lg font-semibold text-gray-800 mb-2">Saved as Draft</h2>
            <p className="text-sm text-gray-500 mb-6">Your progress has been saved. You can find it in the Caller List with a Draft badge.</p>
            <button
              onClick={resetForm}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-6 py-2 rounded-lg transition-colors"
            >
              Start New
            </button>
          </div>
        </div>
      </div>
    );
  }

  const inputProps = { form, touched, errs, focusedField, onChange, onBlur, onFocus: setFocusedField };
  const selectedLT = loanTypes.find(lt => lt.name === form.loanType);
  const reqList = selectedLT?.requiredDocuments || [];

  return (
    <>
      {cameraField && (
        <CameraModal
          field={cameraField}
          label={cameraField}
          onCapture={(field, file) => {
            setReqDocs(prev => ({ ...prev, [field]: file }));
            setDocumentErrs(prev => ({ ...prev, [field]: '' }));
          }}
          onClose={() => setCameraField('')}
        />
      )}
      {toast && (
        <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-[70] px-5 py-3 rounded-xl shadow-lg text-sm font-medium text-white transition-all ${toast.type === 'error' ? 'bg-red-500' : 'bg-green-600'}`}>
          {toast.msg}
        </div>
      )}

      <div className="min-h-screen bg-gray-50">
        {/* Top bar */}
        <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-10">
          <div>
            <p className="text-sm font-semibold text-gray-700">Caller Enquiry Form</p>
            {autoSaving && <p className="text-xs text-blue-500 mt-0.5">Saving draft…</p>}
            {!autoSaving && lastSaved && <p className="text-xs text-green-600 mt-0.5">Draft auto-saved</p>}
          </div>
          <span className="text-xs text-gray-500">{userInfo?.name || ''}</span>
        </div>

        {/* Form body */}
        <div className="max-w-3xl mx-auto px-4 py-6">
          <form key={formKey} onSubmit={submit} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <TextInput field="caseName" label="Case Name *" {...inputProps} />
              <TextInput field="mobileNumber" label="Mobile Number *" placeholder="10-digit mobile number" {...inputProps} />
              <TextInput field="referredBy" label="Ref. by" {...inputProps} />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Loan Type</label>
                <select value={form.loanType} onChange={e => onChange('loanType', e.target.value)} className={inputCls}>
                  <option value="">Select loan type</option>
                  {loanTypes.map(lt => <option key={lt._id} value={lt.name}>{lt.name}</option>)}
                </select>
              </div>
              <AmountInput field="amountRequired" label="Amount Required" {...inputProps} />
            </div>

            <Field field="remarks" label="Remarks" touched={touched} errs={errs} focusedField={focusedField}>
              <div className="relative">
                <textarea
                  rows={3}
                  className={inputCls}
                  placeholder="Remarks"
                  value={form.remarks}
                  onChange={e => onChange('remarks', e.target.value)}
                  onFocus={() => setFocusedField('remarks')}
                  onBlur={e => onBlur('remarks', e.target.value)}
                />
                <FieldIcon field="remarks" multiline />
              </div>
            </Field>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Status</label>
              <select value={form.callStatus} onChange={e => onChange('callStatus', e.target.value)} className={inputCls}>
                <option value="">Select status</option>
                {callStatusOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>

            {form.callStatus === 'call_back' && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Call Back Date *</label>
                <input
                  type="date"
                  className={inputCls}
                  value={form.callBackDate}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={e => onChange('callBackDate', e.target.value)}
                />
              </div>
            )}

            {form.callStatus === 'cold_lead' && (
              <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                <p className="text-xs text-blue-700">
                  Call back will be automatically scheduled 15 days from today ({new Date(Date.now() + 15 * 86400000).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}).
                </p>
              </div>
            )}

            <div>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={!!form.dispositionChecked}
                  onChange={e => onChange('dispositionChecked', e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-semibold text-gray-700">Disposition</span>
              </label>
              {form.dispositionChecked && (
                <textarea
                  rows={2}
                  className={`${inputCls} mt-2`}
                  placeholder="Disposition remark"
                  value={form.dispositionRemark}
                  onChange={e => onChange('dispositionRemark', e.target.value)}
                />
              )}
            </div>

            {/* Document Images */}
            <div>
              <h2 className="text-sm font-semibold text-gray-700 mb-3">Document Images</h2>
              {!form.loanType ? (
                <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-200 rounded-xl bg-white py-10 text-center">
                  <svg className="w-10 h-10 text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m0 0v2m0-2h2m-2 0H10m2-6a4 4 0 100-8 4 4 0 000 8zm-9 9a9 9 0 1118 0H3z" />
                  </svg>
                  <p className="text-sm font-medium text-gray-400">Select a loan type to upload documents</p>
                </div>
              ) : reqList.length === 0 ? (
                <p className="text-xs text-gray-400 italic">No required documents defined for this loan type.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {reqList.map(docName => (
                    <DocumentCapture
                      key={docName}
                      field={docName}
                      label={docName}
                      file={reqDocs[docName] || null}
                      error={documentErrs[docName]}
                      onChange={(field, file) => {
                        setReqDocs(prev => ({ ...prev, [field]: file }));
                        setDocumentErrs(prev => ({ ...prev, [field]: '' }));
                      }}
                      onClear={field => {
                        setReqDocs(prev => ({ ...prev, [field]: null }));
                        setDocumentErrs(prev => ({ ...prev, [field]: '' }));
                      }}
                      onCamera={setCameraField}
                      onError={msg => setToast({ type: 'error', msg })}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="pt-2 flex flex-col gap-3">
              <button
                type="submit"
                disabled={submitting || skipping}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium px-6 py-3 rounded-xl transition-colors"
              >
                {submitting ? 'Submitting…' : 'Submit Enquiry'}
              </button>
              <button
                type="button"
                onClick={skipAsDraft}
                disabled={submitting || skipping}
                className="w-full bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 text-sm font-medium px-6 py-3 rounded-xl border border-gray-300 transition-colors"
              >
                {skipping ? 'Saving draft…' : 'Skip — Save as Draft'}
              </button>
            </div>
          </form>

        </div>
      </div>
    </>
  );
}
